import { getValidMatchingOptions } from "@pathwright/blocks-core/src/blocks/question/type/completion"
import { createID } from "@pathwright/blocks-engine"
import produce from "immer"
import find from "lodash/find"
import uniqBy from "lodash/uniqBy"
import seedrandom from "seedrandom"
import { isEmptyHTML, removeHTML } from "../../utils"

export const CORRECT = "correct"
export const INCORRECT = "incorrect"

export const isEmptyOption = (optionHTML) => {
  return !optionHTML || optionHTML.length <= 0
}

// Return either null (for no answers selected) or correct/incorrect string
export const getAnswerFeedback = (data, isMC = false, t, completion) => {
  const correctLabel = t("blocks.question.correct")
  const incorrectLabel = t("blocks.question.incorrect")

  if (!completion) return null

  return completion.score === 1
    ? correctLabel
    : completion.score === 0
    ? incorrectLabel
    : null
}

// this is a hack to patch for when some blocks don't have a layout set
export const layoutIsValid = (props) => {
  const { layout } = props
  const { answer, type, options, matches } = props.data
  let valid = !!answer

  const typeCheck = (layout || type).toLowerCase()

  switch (typeCheck) {
    case "checkboxes":
      // No answers marked as correct means it's a survey block
      if (options.filter((o) => o.isCorrectAnswer).length === 0) {
        return true
      }
      return valid
    case "multiplechoice":
      return (
        valid && answer.selectedOptions && answer.selectedOptions.length > 0
      )
    case "matching":
      return (
        valid &&
        answer.selectedOptions &&
        answer.selectedOptions.length >=
          getValidMatchingOptions(options, matches).length
      )
    case "paragraph":
      return true
    default:
      return valid && (!!answer.answerText || !!answer.answerHTML)
  }
}

export const getTextFromHTML = (html) => {
  const tempDiv = document.createElement("div")
  tempDiv.innerHTML = html
  return (tempDiv.innerText || "").trim()
}

const removeEmptyOptions = (options) =>
  options.filter((o) => o.label !== "" && o.label !== "<p><br></p>")

// Sanitize options data when switching to Multiple Choice layout
export const getQuestionOptions = (data, isMC) => {
  const { options } = data

  // Options might be undefined if switching from paragraph
  if (!options) return []

  // If checkboxes or mc to mc, return options untouched
  if (!isMC) return removeEmptyOptions(options)

  // When switching from checkboxes w/ multiple answers...
  // arbitrarily retain the first correct answer
  const firstCorrectAnswer = options.find((o) => o.isCorrectAnswer === true)

  // If no correct answer selected (survey-style) then return options as is
  const nextOptions = firstCorrectAnswer
    ? options.map((o, idx) => {
        const existingIndex = options.indexOf(firstCorrectAnswer)
        const isCorrectAnswer = idx === existingIndex
        return {
          ...o,
          isCorrectAnswer
        }
      })
    : options

  return removeEmptyOptions(nextOptions)
}

// Get answered options (limit by question type) or empty array
export const getQuestionSelections = (data, isMC) => {
  if (!data.answer || !data.answer.selectedOptions) return []

  // Remove any selected options that are no longer in the options list
  const cleanSelections = data.answer.selectedOptions.filter((o) => {
    return !!find(data.options, { label: o.label })
  })

  // For Multiple Choice, retain only a single selected answer
  const filterSelections = (ops) => {
    return isMC && ops.length > 1 ? ops.filter((o, i) => !i) : ops
  }

  return filterSelections(cleanSelections)
}

// Use the order property to sort options consistently
export const sortOptions = (options) => {
  return [...options].sort((a, b) => (a.order > b.order ? 1 : -1))
}

// Boilerplate object for a new option
export const generateOption = (order) => {
  return {
    label: "",
    order,
    value: createID()
  }
}

// Don't allow adding new option if not all existing options have content
export const shouldPromptForNew = (options) => {
  return (
    options.filter((op) => !isEmptyOption(op.label)).length === options.length
  )
}

export const getQuestionType = (layout, data) => {
  return (layout || data.type).toLowerCase()
}

export const isMultipleChoice = (layout, data) => {
  const type = getQuestionType(layout, data)
  return type === "multiplechoice"
}

export const isSelected = (op, selectedOptions) => {
  return !!selectedOptions.find(
    (o) => o.value === op.value && o.order === op.order
  )
}

export const getResponseFeedbackType = (op, isChecked, isMC) => {
  if (!op.isCorrectAnswer && isChecked) return INCORRECT

  if (op.isCorrectAnswer && !isChecked && !isMC) return INCORRECT

  return null
}

export const getMatchingExplanationFeedback = (option, matches, t) => {
  if (!option || !matches) return ""

  const correctMatch = matches.find((m) => m.order === option.order)

  return t(`Correct answer: {{answer}}`, {
    answer: removeHTML(correctMatch.label)
  })
}

export const getMCExplanationFeedback = (op, isChecked, isMC, options, t) => {
  if (isMC && !op.isCorrectAnswer && isChecked) {
    const correctAnswer = options.find((o) => o.isCorrectAnswer)
    if (correctAnswer) {
      const labelContent = removeHTML(correctAnswer.label)
      return t(`Correct answer: {{answer}}`, { answer: labelContent })
    } else {
      return null
    }
  }

  if (!isMC && op.isCorrectAnswer && !isChecked) {
    return t("Missed option - Incorrect")
  }
  if (!op.isCorrectAnswer && isChecked) {
    return t("Selected option - Incorrect")
  }

  return null
}

// Mark an answer option as selected/deselected
export const updateAnswerOnSelect = (value, order, data, isMC) => {
  const options = getQuestionOptions(data, isMC)
  const selectedOptions = getQuestionSelections(data, isMC)

  // Find matching option radio/checkbox selection
  const op = options.find(
    (o) => `${o.value}` === `${value}` && o.order === order
  )

  // Update current answer's selected options with incoming selection
  const answer = produce({ selectedOptions }, (draft) => {
    if (!draft.selectedOptions) {
      draft.selectedOptions = []
    }

    // Multiple Choice: only one option selected at a time
    if (isMC) {
      draft.selectedOptions = [op]
      return

      // Checkboxes: toggle on/off
    } else {
      // Deselect
      if (isSelected(op, selectedOptions)) {
        draft.selectedOptions = selectedOptions.filter(
          (op) => op.value !== value || op.order !== order
        )
        // Select
      } else {
        draft.selectedOptions.push(op)
      }
    }
  })

  return answer
}

export const updateOrdering = (options) => {
  return produce(options, (draft) => {
    draft.forEach((c, i) => {
      c.order = i + 1
    })
  })
}

export const ensureValues = (optionsArray) => {
  return produce(optionsArray, (draft) => {
    draft.forEach((o) => {
      if (!o.value) {
        o.value = createID()
      }
    })
  })
}

export const needReordering = (options) => {
  let outOfOrder = false
  options.forEach((option, i) => {
    if (option.order !== i + 1) {
      outOfOrder = true
    }
  })

  return outOfOrder
}

export const normalizeMatchingOrdering = (options, matches) => {
  if (needReordering(options)) {
    options = updateOrdering(options)
  }
  if (needReordering(matches)) {
    matches = updateOrdering(matches)
  }
  return { options, matches }
}

export const normalizeMCOrdering = (options = []) => {
  // return needReordering(options) ? updateOrdering(options) : options
  return updateOrdering(options)
}

// MC/CHECKBOXES - Option CRUD functions

export const changeOption = (index, html, options) => {
  const updatedOptions = produce(options, (draft) => {
    draft[index].label = html
  })
  return updatedOptions
}

export const addOption = (option, options) =>
  produce(options, (draft) => {
    draft.push(option)
  })

export const removeOption = (i, options) => {
  // Always leave one option
  if (options.length <= 1) return options

  const updatedOptions = produce(options, (draft) => {
    draft.splice(i, 1)
  })
  return updatedOptions
}

export const toggleCorrectAnswer = (i, options, isMC) => {
  const updatedOptions = produce(options, (draft) => {
    // Only one question should be correct for multiple choice questions
    if (isMC) {
      if (!draft[i].isCorrectAnswer) {
        draft.forEach((o, i) => {
          o.isCorrectAnswer = false
        })
        draft[i].isCorrectAnswer = true
      } else {
        // Allow unselection for "survey mode" case
        draft[i].isCorrectAnswer = !draft[i].isCorrectAnswer
      }
    } else {
      // For checkboxes, simply switch flag
      draft[i].isCorrectAnswer = !draft[i].isCorrectAnswer
    }
  })

  return updatedOptions
}

export const getAnswerMatches = (data) => {
  const { answer, matches = [] } = data

  const answerMatches = answer?.selectedOptions || []

  // Ensure all matches in answer are in the question matches set
  return answerMatches.filter(
    (aMatch) =>
      !!matches.find(
        (match) =>
          getTextFromHTML(aMatch.label) === getTextFromHTML(match.label)
      )
  )
}

export const matchesToSelectOptions = (dataArray) => {
  return uniqBy(
    dataArray
      .filter((m) => !isEmptyHTML(m.label))
      .map((m) => ({
        ...m,
        label: getTextFromHTML(m.label),
        value: m.value || `temp-${m.order}`
      })),
    (m) => m.label
  )
}

export const randomizeMatchesWithUserID = (userID) => (a, b) => {
  if (!userID) {
    console.warn("No userID provided for randomization")
  }
  const randomSeed = seedrandom(
    `${userID || ""}${a.value || a.order}${b.value || b.order}`
  )
  return 0.5 - randomSeed()
}

export const generateMatchingItem = ({ order }) => {
  return {
    label: "",
    order,
    value: createID()
  }
}

// Each row contains one option and one match
export const getMatchingRows = ({ options, matches }) => {
  return options.reduce((rows, option, index) => {
    const order = index + 1
    const row = { order }
    row.option = { ...option, order }
    row.match = { ...matches[index], order }
    rows.push(row)
    return rows
  }, [])
}
